import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AppConfiguration } from "read-appsettings-json";
import NavBar from "../../Layout/CommonLayout/NavBar";
import "./jobspace-account.css";

/*
  Standalone "My Applications" page — the design previously lived inline in
  JobSpaceAccount; extracted here and routed from the NavBar. Read-only: it
  GETs the signed-in candidate's rows from the Candidates collection (the same
  call the account shell used) and renders them. No writes.
*/

// ── mongo parse + GET (same pattern as the rest of the app) ──
const parseMongo = (itm) => {
  if (itm == null) return null;
  if (typeof itm !== "string") return itm;
  try {
    const cleaned = itm.replace(/ObjectId\(([^)]*)\)/g, "$1").replace(/_id/g, "id");
    return JSON.parse(cleaned.replace(/'/g, '"'));
  } catch (e) { return null; }
};
const parseList = (arr) => (Array.isArray(arr) ? arr.map(parseMongo).filter(Boolean) : []);
const getCollection = (collection, filter) =>
  axios
    .post(AppConfiguration.Setting().jobspaceapi + "action/get", { _collection: collection, filter_data: filter || {} })
    .then((res) => parseList((res.data || {})[collection]))
    .catch(() => []);

// ── brand monogram + stage mapping (mirrors JobSpaceAccount) ──
const LOGO_PALETTE = [
  { logoBg: "#e8f0ff", logoColor: "#2563d8" }, { logoBg: "#e6f7ee", logoColor: "#00a862" },
  { logoBg: "#eef0ff", logoColor: "#5b3df5" }, { logoBg: "#fff0e6", logoColor: "#e8742a" },
  { logoBg: "#eafaf1", logoColor: "#0a9d58" }, { logoBg: "#fff0ec", logoColor: "#ee4d2d" },
  { logoBg: "#f1ecff", logoColor: "#7c3aed" }, { logoBg: "#ffecec", logoColor: "#d23b30" },
];
const brandOf = (name) => {
  const s = (name || "?").trim();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % LOGO_PALETTE.length;
  return { initial: (s[0] || "?").toUpperCase(), ...LOGO_PALETTE[h] };
};
const titleCase = (s) => (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const STATUS_TO_STAGE = { NW: "Applied", PS: "Screening", KW: "Screening", SL: "Interview", NQ: "Applied", Process: "Offer" };
const NEXT_STEP = { Applied: "Awaiting review", Screening: "Recruiter screening", Interview: "Interview stage", Offer: "Offer under review" };
const STAGE_ORDER = ["Applied", "Screening", "Interview", "Offer"];
const STAGE_DOT = { Applied: "#9aa89f", Screening: "#e0a020", Interview: "#2563d8", Offer: "#009b57" };
const BADGE_STYLE = {
  Applied: { bg: "#eef2ee", color: "#5f7068" }, Screening: { bg: "#fdf3e0", color: "#a8690f" },
  Interview: { bg: "#e8f0ff", color: "#2563d8" }, Offer: { bg: "#ecf7f0", color: "#047a45" },
};

const Ic = ({ s = 18, children }) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none">{children}</svg>);
const Check = ({ stroke = "#fff", s = 12, w = 2.4 }) => (<Ic s={s}><path d="M20 6 9 17l-5-5" stroke={stroke} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" /></Ic>);

const mapApplication = (itm) => {
  const stage = STATUS_TO_STAGE[itm.status] || "Applied";
  const company = itm.companyName || "—";
  return {
    id: itm.id || itm.postingId || itm.jobDocNo || (itm.title || "") + itm.dateApplied,
    title: itm.title || "Application", company, ...brandOf(company),
    location: itm.city ? titleCase(itm.city) : "—",
    stage, applied: itm.dateApplied || "—", nextStep: NEXT_STEP[stage] || "Awaiting review",
  };
};

const JobSpaceApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [appStageFilter, setAppStageFilter] = useState("All");

  const sessionUser = (() => {
    try { return (JSON.parse(sessionStorage.getItem("userCollection")) || [])[0] || null; }
    catch (e) { return null; }
  })();
  const userId = sessionUser ? sessionUser.userid : null;

  useEffect(() => {
    if (!userId) return;
    getCollection("Candidates", { employeeId: userId, isDeleted: "0" }).then((rows) =>
      setApplications(rows.map(mapApplication))
    );
  }, [userId]);

  const stageStats = STAGE_ORDER.map((st) => ({ label: st, dot: STAGE_DOT[st], count: applications.filter((a) => a.stage === st).length }));
  const filteredApps = applications.filter((a) => appStageFilter === "All" || a.stage === appStageFilter);

  return (
    <div className="jsa-root" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: "#f4f7f4", minHeight: "100vh", color: "#16271f" }}>
      <NavBar />
      <div style={{ height: 68 }} />
      <main style={{ maxWidth: 820, margin: "0 auto", padding: "28px 24px 80px" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: "-.02em" }}>My Applications</h1>
        <p style={{ margin: "5px 0 0", fontSize: 14, color: "#6c7d74", fontWeight: 500 }}>{applications.length} applications · keep track of where you stand</p>

        <div className="jsa-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 11, margin: "22px 0 20px" }}>
          {stageStats.map((ss) => (
            <div key={ss.label} style={{ background: "#fff", border: "1.5px solid #e8ede9", borderRadius: 14, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: ss.dot }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#6c7d74" }}>{ss.label}</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", marginTop: 8 }}>{ss.count}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 2 }}>
          {["All"].concat(STAGE_ORDER).map((st) => {
            const active = appStageFilter === st;
            return <button key={st} onClick={() => setAppStageFilter(st)} style={{ flex: "none", padding: "8px 15px", borderRadius: 100, border: "1.5px solid " + (active ? "#009b57" : "#dde5df"), background: active ? "#009b57" : "#fff", color: active ? "#fff" : "#3c4d44", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{st}</button>;
          })}
        </div>

        {filteredApps.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {filteredApps.map((a) => {
              const si = STAGE_ORDER.indexOf(a.stage);
              const bs = BADGE_STYLE[a.stage] || BADGE_STYLE.Applied;
              return (
                <div key={a.id} style={{ background: "#fff", border: "1.5px solid #e8ede9", borderRadius: 16, padding: 20 }}>
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ flex: "none", width: 48, height: 48, borderRadius: 12, background: a.logoBg, color: a.logoColor, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 19 }}>{a.initial}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, letterSpacing: "-.01em", lineHeight: 1.3 }}>{a.title}</h3>
                          <div style={{ marginTop: 2, fontSize: 13.5, color: "#45564c", fontWeight: 600 }}>{a.company} · {a.location}</div>
                        </div>
                        <span style={{ flex: "none", fontSize: 12, fontWeight: 800, padding: "5px 11px", borderRadius: 100, background: bs.bg, color: bs.color }}>{a.stage}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", margin: "18px 0 6px" }}>
                    {[0, 1, 2].map((i) => {
                      const done = si > i, current = si === i;
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                          <div style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: 0 }}>
                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: (done || current) ? "#009b57" : "#fff", border: "2px solid " + ((done || current) ? "#009b57" : "#d3ddd6"), display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {done && <Check stroke="#fff" s={11} w={3} />}
                              {current && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                            </div>
                          </div>
                          <div style={{ flex: 1, height: 2.5, background: si > i ? "#009b57" : "#e3e9e4" }} />
                        </div>
                      );
                    })}
                    <div style={{ flex: "none", width: 22, height: 22, borderRadius: "50%", background: si >= 3 ? "#009b57" : "#fff", border: "2px solid " + (si >= 3 ? "#009b57" : "#d3ddd6"), display: "flex", alignItems: "center", justifyContent: "center" }}>{si >= 3 && <Check stroke="#fff" s={11} w={3} />}</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 700, color: "#8a9a91" }}>
                    <span>Applied</span><span>Screening</span><span>Interview</span><span>Offer</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f4f1" }}>
                    <span style={{ fontSize: 12.5, color: "#8a9a91", fontWeight: 600 }}>Applied {a.applied}</span>
                    <span style={{ fontSize: 12.5, color: "#047a45", fontWeight: 700 }}>{a.nextStep}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ minHeight: "34vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "30px 0" }}>
            <div style={{ width: 70, height: 70, borderRadius: "50%", background: "#ecf2ee", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}><Ic s={32}><path d="M14 3v5h5" stroke="#9aab9f" strokeWidth="2" strokeLinejoin="round" /><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" stroke="#9aab9f" strokeWidth="2" strokeLinejoin="round" /></Ic></div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#16271f" }}>{userId ? "No applications here yet" : "Sign in to see your applications"}</h3>
            <p style={{ margin: "8px 0 22px", fontSize: 14, color: "#6c7d74", fontWeight: 500 }}>Find a role and hit Apply — it'll show up here.</p>
            <button onClick={() => navigate("/?view=jobs")} className="jsa-btn-primary" style={{ padding: "11px 22px", fontSize: 14 }}>Browse jobs</button>
          </div>
        )}
      </main>
    </div>
  );
};

export default JobSpaceApplications;
